import { finalize, mergeMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { RouterSelectors, ToastService } from '@mitsubishi/core';

import { DataAction, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Action, State, StateContext, Store } from '@ngxs/store';

import { UnitTemplateFilters, UnitTemplateGroup, UnitTemplateGroups, UnitTemplatesWithFilters } from '../../models';
import { UnitTemplateService } from '../../services';
import { UnitTemplateActions } from './unit-template.actions';
import { PROJECT_UNIT_TEMPLATE_STATE_TOKEN, UnitSelectionStateModel } from './unit-template.model';

const PAGE_SIZE = 20;

const defaults = {
  unitTemplateWithFilters: { list: [], filters: [] },
  groups: { personalGroups: [], organizationGroups: [] },
  selectedGroup: { id: '', name: '' },
  filters: {
    search: null,
    brandId: null,
    segmentId: null,
    classId: null,
  },
  pagination: { page: 0, pageSize: PAGE_SIZE, total: 0 },
  isLoadingMore: false,
};

@StateRepository()
@State<UnitSelectionStateModel>({
  name: PROJECT_UNIT_TEMPLATE_STATE_TOKEN,
  defaults,
})
@Injectable({
  providedIn: 'root',
})
export class ProjectUnitTemplateState extends NgxsDataRepository<UnitSelectionStateModel> {
  constructor(
    private unitTemplateService: UnitTemplateService,
    private toastService: ToastService,
    private store: Store
  ) {
    super();
  }

  @Action(UnitTemplateActions.Load)
  public load(ctx: StateContext<UnitSelectionStateModel>) {
    const projectId = this.getCurrentProjectId();
    const { filters, selectedGroup } = this.ctx.getState();
    return this.unitTemplateService.getUnitTemplates(projectId, filters, selectedGroup, 0, PAGE_SIZE).pipe(
      tap(response => {
        ctx.patchState({
          unitTemplateWithFilters: { list: response.data, filters: [] },
          pagination: { page: response.page, pageSize: response.pageSize, total: response.total },
          isLoadingMore: false,
        });
      })
    );
  }

  @Action(UnitTemplateActions.LoadMore)
  public loadMore(ctx: StateContext<UnitSelectionStateModel>) {
    const state = ctx.getState();
    const loaded = state.unitTemplateWithFilters.list.length;
    if (state.isLoadingMore || loaded >= state.pagination.total) {
      return;
    }
    ctx.patchState({ isLoadingMore: true });
    const projectId = this.getCurrentProjectId();
    const nextPage = state.pagination.page + 1;
    return this.unitTemplateService
      .getUnitTemplates(projectId, state.filters, state.selectedGroup, nextPage, state.pagination.pageSize)
      .pipe(
        tap(response => {
          const current = ctx.getState();
          ctx.patchState({
            unitTemplateWithFilters: {
              list: [...current.unitTemplateWithFilters.list, ...response.data],
              filters: [],
            },
            pagination: { page: response.page, pageSize: response.pageSize, total: response.total },
          });
        }),
        finalize(() => ctx.patchState({ isLoadingMore: false }))
      );
  }

  @Action(UnitTemplateActions.Groups)
  public loadUnitTemplateGroups(ctx: StateContext<UnitSelectionStateModel>) {
    return this.unitTemplateService.getUnitTemplateGroups().pipe(
      tap(response => {
        ctx.patchState({
          groups: {
            organizationGroups: response.organizationGroups,
            personalGroups: response.personalGroups.map(group => ({ ...group, isPersonal: true })),
          },
        });
      })
    );
  }

  @Action(UnitTemplateActions.AddUnitTemplateGroup)
  public addUnitTemplateGroup(
    ctx: StateContext<UnitSelectionStateModel>,
    { name, isOrganization }: UnitTemplateActions.AddUnitTemplateGroup
  ) {
    return this.unitTemplateService
      .addUnitGroup(name, isOrganization)
      .pipe(mergeMap(() => ctx.dispatch(new UnitTemplateActions.Groups())));
  }

  @Action(UnitTemplateActions.UpdateGroupTemplateName)
  public updateUnitGroupName(
    ctx: StateContext<UnitSelectionStateModel>,
    { groupId, description }: UnitTemplateActions.UpdateGroupTemplateName
  ) {
    return this.unitTemplateService
      .updateUnitGroupName(groupId, description)
      .pipe(mergeMap(() => ctx.dispatch(new UnitTemplateActions.Groups())));
  }

  @Action(UnitTemplateActions.DeleteGroupTemplate)
  public deleteUnitGroup(
    ctx: StateContext<UnitSelectionStateModel>,
    { groupId }: UnitTemplateActions.DeleteGroupTemplate
  ) {
    return this.unitTemplateService
      .deleteUnitGroup(groupId)
      .pipe(mergeMap(() => ctx.dispatch(new UnitTemplateActions.Groups())));
  }

  @Action(UnitTemplateActions.Delete)
  public deleteUnitTemplate(
    ctx: StateContext<UnitSelectionStateModel>,
    { unitTemplateUuid }: UnitTemplateActions.Delete
  ) {
    return this.unitTemplateService.deleteUnitTemplate(unitTemplateUuid).pipe(
      tap(() => this.unitTemplateService.bustUnitTemplatesCache()),
      mergeMap(() => ctx.dispatch(new UnitTemplateActions.Load()))
    );
  }

  @Action(UnitTemplateActions.Create)
  public createUnitTemplate(
    ctx: StateContext<UnitSelectionStateModel>,
    { unitId, unitTemplateName, groupId, alternativeId }: UnitTemplateActions.Create
  ) {
    const projectId = this.store.selectSnapshot(RouterSelectors.params).projectId;
    return this.unitTemplateService
      .createUnitTemplate(projectId, unitId, unitTemplateName, groupId, alternativeId)
      .pipe(
        tap(() => {
          this.toastService.showToast({ resourceId: '445226' });
          this.unitTemplateService.bustUnitTemplatesCache();
        })
      );
  }

  @Action(UnitTemplateActions.UpdateUnitDescription)
  public updateUnitDescription(
    ctx: StateContext<UnitSelectionStateModel>,
    { unitTemplateId, description }: UnitTemplateActions.UpdateUnitDescription
  ) {
    return this.unitTemplateService.updateUnitDescription(unitTemplateId, description);
  }

  @Action(UnitTemplateActions.MoveUnit)
  public moveUnit(
    ctx: StateContext<UnitSelectionStateModel>,
    { unitTemplateId, groupId }: UnitTemplateActions.MoveUnit
  ) {
    return this.unitTemplateService.moveUnit(unitTemplateId, groupId).pipe(
      tap(() => {
        ctx.dispatch(UnitTemplateActions.Load);
      })
    );
  }

  @Action(UnitTemplateActions.SetSelectedGroup)
  public setSelectedGroup(
    ctx: StateContext<UnitSelectionStateModel>,
    { selectedGroup }: UnitTemplateActions.SetSelectedGroup
  ) {
    ctx.patchState({
      selectedGroup: selectedGroup,
    });
    ctx.dispatch(UnitTemplateActions.Load);
  }

  @Action(UnitTemplateActions.ClearState)
  public clearState(ctx: StateContext<UnitSelectionStateModel>) {
    ctx.setState(defaults);
  }

  @DataAction()
  public setFilter(fieldId: string, value: string | number | null) {
    const newFilters: any = { ...this.getState().filters };
    newFilters[fieldId] = value;
    this.ctx.setState(state => ({ ...state, filters: newFilters }));
    //this.ctx.dispatch(UnitTemplateActions.Load);
  }

  private getCurrentProjectId() {
    return this.store.selectSnapshot(RouterSelectors.params).projectId;
  }
}
